import axios from 'axios';
import router from '@/router';
import store from '@/store';

import config from '@/config';

const AUTH_API_URL = config.AUTH_API;



const apiAuth = axios.create({
  baseURL: AUTH_API_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

// Función para obtener el token de acceso
function getAccessToken() {
  try {
    const storeToken = store.getters.accessToken;
    const localToken = localStorage.getItem('access_token');
    const token = storeToken || localToken;
    return token;
  } catch {
    const token = localStorage.getItem('access_token');
    return token;
  }
}

// Función para obtener el refresh token
function getRefreshToken() {
  try {
    return store.getters.refreshToken || localStorage.getItem('refresh_token');
  } catch {
    return localStorage.getItem('refresh_token');
  }
}

// Función para limpiar tokens y redirigir al login
function clearAuthAndRedirect() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_profile');
  store.commit('reset_credentials');

  // Verificar la ruta actual antes de redirigir para evitar navegación redundante
  if (router.currentRoute.path !== '/login') {
    router.push('/login');
  }
}

// Interceptor de solicitud - Agregar token y headers automáticamente
apiAuth.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    const appId = localStorage.getItem('app_id') || config.APP_ID;
    const tenantId = localStorage.getItem('tenant_id') || config.TENANT_ID;

    // Verificar que los headers no sean undefined
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Agregar headers requeridos con validación
    config.headers['X-App-ID'] = appId || 'medica';
    config.headers['X-Tenant-ID'] = tenantId || '550e8400-e29b-41d4-a716-446655440001';

    return config;
  },
  (error) => {
    console.error('Error en interceptor de request:', error);
    return Promise.reject(error);
  }
);

// Interceptor de respuesta - Manejar errores de autenticación
apiAuth.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si es error 401 y no hemos intentado refresh ya
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Intentar refresh del token con timeout específico
        const refreshResponse = await axios.post(`${AUTH_API_URL}/sessions/refresh`, {
          refresh_token: refreshToken,
          app_id: localStorage.getItem('app_id') || config.APP_ID,
          tenant_id: localStorage.getItem('tenant_id') || config.TENANT_ID
        }, {
          timeout: 5000, // Timeout específico para refresh
          headers: {
            'X-App-ID': localStorage.getItem('app_id') || config.APP_ID,
            'X-Tenant-ID': localStorage.getItem('tenant_id') || config.TENANT_ID
          }
        });

        const { access_token, refresh_token } = refreshResponse.data;

        if (!access_token) {
          throw new Error('No se recibió access token en el refresh');
        }

        // Guardar nuevos tokens
        localStorage.setItem('access_token', access_token);
        if (refresh_token) {
          localStorage.setItem('refresh_token', refresh_token);
        }

        store.commit('set_credentials', {
          ...store.state.credentials,
          access_token,
          refresh_token: refresh_token || refreshToken
        });

        // Reintentar la request original con el nuevo token
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return apiAuth(originalRequest);

      } catch (refreshError) {
        // Si el refresh falla, limpiar auth y redirigir
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    // Para otros errores, mostrar mensaje apropiado
    if (error.response && error.response.status === 403) {
      console.error('Acceso denegado - Usuario sin permisos');
    }

    if (error.response && error.response.status >= 500) {
      console.error('Error del servidor auth - Status:', error.response.status);
    }

    if (error.code === 'ECONNABORTED') {
      console.error('Timeout en petición auth');
    }

    return Promise.reject(error);
  }
);

// Función para verificar la salud del Auth API
export async function checkAuthHealth() {
  try {
    const response = await axios.get(`${AUTH_API_URL}/health`, { timeout: 3000 });
    return true;
  } catch (error) {
    console.error('Auth API no disponible:', error.message);
    return false;
  }
}

// Función para verificar conectividad antes de hacer requests críticos
export async function ensureAuthConnectivity() {
  const isHealthy = await checkAuthHealth();
  if (!isHealthy) {
    throw new Error('Auth Service no está disponible');
  }
  return true;
}

export default apiAuth; 